
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Stack,
  Fade,
  LinearProgress,
  useMediaQuery
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getLogs, getRestaurantes } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

import {
  normalizeMetadata,
  PAGE_BG,
  CARD_SX,
  makeIconBox,
  summarizeQueueMetadata,
  deriveRestauranteId,
  deriveClienteTelefone,
  formatCount,
  getRowClassName,
} from './logsPage.utils';
import LogsSummaryCards from '../components/LogsSummaryCards';
import LogsFiltersPanel from '../components/LogsFiltersPanel';
import LogsBottomPanels from '../components/LogsBottomPanels';
import LogsDetailModal from '../components/LogsDetailModal';
import { buildLogsColumns } from '../components/LogsDataGridColumns';


const LogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Filters
  const [filterLevel, setFilterLevel] = useState('ALL');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterStep, setFilterStep] = useState([]);
  const [filterRestaurante, setFilterRestaurante] = useState('ALL');
  const [filterTraceId, setFilterTraceId] = useState('');
  const [filterOrigin, setFilterOrigin] = useState('ALL');
  const [filterIgnored, setFilterIgnored] = useState('ALL');
  const [groupByPhone, setGroupByPhone] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: 'created_at', sort: 'desc' }]);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Restaurants data
  const [restaurantes, setRestaurantes] = useState([]);

  // Modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const isTablet = useMediaQuery('(max-width:1024px)');
  const isMobile = useMediaQuery('(max-width:680px)');
  const dataGridRowHeight = isMobile ? 108 : isTablet ? 88 : 74;

  // Fetch restaurants on component mount
  useEffect(() => {
    const fetchRestaurantes = async () => {
      try {
        const data = await getRestaurantes();
        setRestaurantes(data || []);
      } catch (error) {
        console.error('Error fetching restaurants:', error);
      }
    };
    fetchRestaurantes();
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1, // Backend expects 1-based index
        pageSize: paginationModel.pageSize,
        level: filterLevel !== 'ALL' ? filterLevel : undefined,
        phone: filterPhone || undefined,
        step: filterStep.length > 0 ? filterStep.join(',') : undefined,
        restaurante_id: filterRestaurante !== 'ALL' ? filterRestaurante : undefined,
        trace_id: filterTraceId || undefined,
        origin: filterOrigin !== 'ALL' ? filterOrigin : undefined,
        ignored: filterIgnored !== 'ALL' ? filterIgnored : undefined,
        sortOrder: sortModel[0]?.sort || 'desc',
      };

      const { data, count } = await getLogs(params);

      // Client-side grouping if enabled (simple sort by phone then date)
      let processedLogs = data || [];
      if (groupByPhone) {
        processedLogs = [...processedLogs].sort((a, b) => {
          if (a.cliente_telefone === b.cliente_telefone) {
            return new Date(b.created_at) - new Date(a.created_at);
          }
          return (a.cliente_telefone || '').localeCompare(b.cliente_telefone || '');
        });
      }

      // Sinalizar traces ignorados (bloqueados) para aplicar bolinha vermelha em todos os eventos do mesmo trace
      const ignoredTraceIds = new Set();
      processedLogs.forEach((log) => {
        const metadata = normalizeMetadata(log.metadata);
        if (metadata?.ignored && log.trace_id) {
          ignoredTraceIds.add(log.trace_id);
        }
      });

      const enrichedLogs = processedLogs.map((log) => {
        const normalizedMetadata = normalizeMetadata(log.metadata);
        const isTraceIgnored = log.trace_id ? ignoredTraceIds.has(log.trace_id) : false;
        const isSelfIgnored = normalizedMetadata?.ignored === true;
        return {
          ...log,
          _metadataNormalized: normalizedMetadata,
          _isTraceIgnored: isTraceIgnored || isSelfIgnored,
        };
      });

      let finalLogs = enrichedLogs;
      if (filterIgnored === 'VALID') {
        finalLogs = enrichedLogs.filter((log) => !log._isTraceIgnored);
      } else if (filterIgnored === 'IGNORED') {
        finalLogs = enrichedLogs.filter((log) => log._isTraceIgnored);
      }

      setLogs(finalLogs);
      setTotal(filterIgnored === 'ALL' ? (count || finalLogs.length) : finalLogs.length);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterLevel, filterPhone, filterStep, filterRestaurante, filterTraceId, filterOrigin, filterIgnored, groupByPhone, sortModel]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleOpenModal = useCallback((log) => {
    setSelectedLog(log);
    setOpenModal(true);
  }, []);

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedLog(null);
  };

  const selectedMetadata = useMemo(() => normalizeMetadata(selectedLog?.metadata), [selectedLog]);
  const selectedQueueInfo = useMemo(() => summarizeQueueMetadata(selectedMetadata), [selectedMetadata]);
  const selectedStageTimings = useMemo(
    () => (Array.isArray(selectedMetadata?.stage_timings) ? selectedMetadata.stage_timings : []),
    [selectedMetadata]
  );

  const selectedPhone = useMemo(() => (selectedLog ? deriveClienteTelefone(selectedLog) : null), [selectedLog]);
  const selectedRestauranteId = useMemo(() => (selectedLog ? deriveRestauranteId(selectedLog) : null), [selectedLog]);

  const levelSummary = useMemo(() => {
    const summary = { ERROR: 0, WARN: 0, INFO: 0, totalPage: logs.length, totalGlobal: total, uniquePhones: 0 };
    const phones = new Set();

    logs.forEach((log) => {
      if (log?.level) {
        summary[log.level] = (summary[log.level] || 0) + 1;
      }
      const phone = deriveClienteTelefone(log);
      if (phone) phones.add(phone);
    });

    summary.uniquePhones = phones.size;
    summary.errorRatio = summary.totalPage > 0 ? Math.round((summary.ERROR / summary.totalPage) * 100) : 0;
    return summary;
  }, [logs, total]);

  const averageDurationSeconds = useMemo(() => {
    const durations = logs
      .map((log) => (typeof log.duration_ms === 'number' ? log.duration_ms : null))
      .filter((value) => typeof value === 'number');
    if (!durations.length) return 0;
    return (durations.reduce((acc, value) => acc + value, 0) / durations.length) / 1000;
  }, [logs]);

  const topSteps = useMemo(() => {
    const counts = {};
    logs.forEach((log) => {
      if (!log?.step) return;
      counts[log.step] = (counts[log.step] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
  }, [logs]);

  const topPhones = useMemo(() => {
    const counts = {};
    logs.forEach((log) => {
      const phone = deriveClienteTelefone(log);
      if (!phone) return;
      counts[phone] = (counts[phone] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  }, [logs]);

  const recentErrors = useMemo(
    () => logs.filter((log) => log.level === 'ERROR').slice(0, 3),
    [logs]
  );

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdated) return 'Aguardando atualização';
    return formatDistanceToNow(lastUpdated, { addSuffix: true });
  }, [lastUpdated]);

  const handleQuickLevelFilter = (level) => {
    setFilterLevel(level);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleQuickStepFilter = (step) => {
    setFilterStep(step ? [step] : []);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleQuickPhoneFilter = (phone) => {
    setFilterPhone(phone || '');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilterLevel('ALL');
    setFilterPhone('');
    setFilterStep([]);
    setFilterRestaurante('ALL');
    setFilterTraceId('');
    setFilterOrigin('ALL');
    setFilterIgnored('ALL');
    setGroupByPhone(false);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = useMemo(
    () =>
      buildLogsColumns({
        restaurantes,
        handleOpenModal,
        setFilterTraceId,
        filterTraceId,
      }),
    [restaurantes, handleOpenModal, filterTraceId]
  );


  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        minHeight: '100vh',
        background: PAGE_BG,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* ── Header card ── */}
      <Fade in timeout={400}>
        <Box sx={CARD_SX}>
          <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 2.5, md: 2 }}
              alignItems={{ md: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Stack direction="row" alignItems="center" gap={1.5} mb={1}>
                  <Box sx={{ ...makeIconBox('#6366f1', 'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)', 'rgba(99,102,241,0.18)'), width: 48, height: 48, borderRadius: '14px' }}>
                    <BugReportIcon sx={{ fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={800} sx={{ color: '#0f172a', fontSize: '1.25rem', letterSpacing: '-0.02em', lineHeight: 1.2, mb: 0.25 }}>
                      Logs do Atendabot
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Monitoramento em tempo real para localizar qualquer comportamento estranho antes que afete o cliente.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                    label={`Atualizado ${lastUpdatedLabel}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(99,102,241,0.08)',
                      color: '#6366f1',
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      border: '1px solid rgba(99,102,241,0.18)',
                      '& .MuiChip-icon': { color: '#6366f1' },
                    }}
                  />
                  <Chip
                    icon={<InfoOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                    label={`${formatCount(levelSummary.uniquePhones)} telefones monitorados`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(8,145,178,0.08)',
                      color: '#0891b2',
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      border: '1px solid rgba(8,145,178,0.18)',
                      '& .MuiChip-icon': { color: '#0891b2' },
                    }}
                  />
                </Stack>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={fetchLogs}
                  sx={{
                    borderRadius: '12px', fontWeight: 700, boxShadow: 'none',
                    bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' },
                  }}
                >
                  Atualizar
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ErrorOutlineIcon />}
                  onClick={() => handleQuickLevelFilter('ERROR')}
                  sx={{
                    borderRadius: '12px', fontWeight: 600,
                    borderColor: '#fecdd3', color: '#be123c',
                    '&:hover': { bgcolor: '#fff1f2', borderColor: '#fca5a5' },
                  }}
                >
                  Somente erros
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<InfoOutlinedIcon />}
                  onClick={() => handleQuickLevelFilter('ALL')}
                  sx={{
                    borderRadius: '12px', fontWeight: 600,
                    borderColor: 'rgba(99,102,241,0.30)', color: '#6366f1',
                    '&:hover': { bgcolor: 'rgba(99,102,241,0.06)', borderColor: '#6366f1' },
                  }}
                >
                  Ver todos
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Fade>

      <LogsSummaryCards
        levelSummary={levelSummary}
        averageDurationSeconds={averageDurationSeconds}
      />

      <LogsFiltersPanel
        restaurantes={restaurantes}
        filterRestaurante={filterRestaurante}
        setFilterRestaurante={setFilterRestaurante}
        filterLevel={filterLevel}
        setFilterLevel={setFilterLevel}
        filterOrigin={filterOrigin}
        setFilterOrigin={setFilterOrigin}
        filterIgnored={filterIgnored}
        setFilterIgnored={setFilterIgnored}
        filterPhone={filterPhone}
        setFilterPhone={setFilterPhone}
        filterStep={filterStep}
        setFilterStep={setFilterStep}
        filterTraceId={filterTraceId}
        setFilterTraceId={setFilterTraceId}
        groupByPhone={groupByPhone}
        setGroupByPhone={setGroupByPhone}
        handleClearFilters={handleClearFilters}
      />

      {/* ── DataGrid card ── */}
      <Fade in timeout={700}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box
            sx={{
              ...CARD_SX,
              flex: 1,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 420, md: 520 },
            }}
          >
            {loading && <LinearProgress color="primary" />}
            <Box sx={{ flex: 1, overflowX: isTablet ? 'auto' : 'visible' }}>
              <Box sx={{ minWidth: isTablet ? 780 : 'auto', height: '100%', display: 'flex' }}>
                <DataGrid
                  rows={logs}
                  columns={columns}
                  rowCount={total}
                  loading={loading}
                  pageSizeOptions={[25, 50, 100]}
                  paginationModel={paginationModel}
                  paginationMode="server"
                  onPaginationModelChange={setPaginationModel}
                  sortingMode="server"
                  sortModel={sortModel}
                  onSortModelChange={setSortModel}
                  getRowHeight={() => dataGridRowHeight}
                  getRowClassName={getRowClassName}
                  disableRowSelectionOnClick
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{ toolbar: { showQuickFilter: false } }}
                  sx={{
                    flex: 1,
                    border: 0,
                    backgroundColor: '#fff',
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#fafafa',
                      fontWeight: 'bold',
                      letterSpacing: 0.5,
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      backgroundColor: '#fff',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#eef2ff',
                    },
                    '& .log-row-user': {
                      backgroundColor: '#e3f2fd',
                      fontWeight: '500',
                    },
                    '& .log-row-ai': {
                      backgroundColor: '#f3e5f5',
                    },
                    '& .log-row-processor': {
                      backgroundColor: '#e0f7fa',
                    },
                    '& .log-row-queue': {
                      backgroundColor: '#fff3e0',
                    },
                    '& .log-row-lock': {
                      backgroundColor: '#eceff1',
                    },
                    '& .log-row-restaurant': {
                      backgroundColor: '#e8f5e9',
                      fontWeight: '500',
                    },
                    '& .log-row-restaurant-webhook': {
                      backgroundColor: '#bbdefb',
                      fontWeight: '500',
                    },
                    '& .log-row-error': {
                      backgroundColor: '#ffebee',
                    },
                    '& .log-row-warn': {
                      backgroundColor: '#fff3e0',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>

      <LogsBottomPanels
        recentErrors={recentErrors}
        topSteps={topSteps}
        topPhones={topPhones}
        handleOpenModal={handleOpenModal}
        setFilterTraceId={setFilterTraceId}
        handleQuickStepFilter={handleQuickStepFilter}
        handleQuickPhoneFilter={handleQuickPhoneFilter}
      />

      <LogsDetailModal
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        selectedLog={selectedLog}
        selectedMetadata={selectedMetadata}
        selectedQueueInfo={selectedQueueInfo}
        selectedStageTimings={selectedStageTimings}
        selectedPhone={selectedPhone}
        selectedRestauranteId={selectedRestauranteId}
        setFilterTraceId={setFilterTraceId}
      />
    </Box>
  );
};

export default LogsPage;
